import { useState } from "react";
import { useNotify } from "ra-core";
import { useSearchParams } from "react-router";
import { Download, Landmark, Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { EmptyState } from "../misc/EmptyState";
import { pruefe, sb, useBuchhaltung, useZugang } from "./daten";
import { taktAm } from "./kern/fristen";
import { JAHR_VORGABE, type Takt } from "./kern/typen";
import { AuswertungTab } from "./AuswertungTab";
import { EinnahmenUeberschuss } from "./EinnahmenUeberschuss";
import { ErfassungTab } from "./ErfassungTab";
import { FahrtenTab } from "./FahrtenTab";
import { ImportTab } from "./ImportTab";
import { RueckfragenTab } from "./RueckfragenTab";
import { SetupTab } from "./SetupTab";
import { Auswahl, heute, type TabProps } from "./teile";

const TAKT_NAME: Record<Takt, string> = {
  monatlich: "monatlich",
  vierteljaehrlich: "vierteljährlich",
  befreit: "befreit",
  offen: "offen",
};

const BEREICHE = [
  ["setup", "Setup"],
  ["import", "Import"],
  ["erfassung", "Erfassung"],
  ["rueckfragen", "Rückfragen"],
  ["fahrten", "Fahrten"],
  ["auswertung", "Auswertung"],
] as const;

export const BuchhaltungPage = () => {
  const { zugang, isLoading } = useZugang();
  if (isLoading) return null;
  if (!zugang) {
    return (
      <EmptyState
        Icon={Lock}
        title="Kein Zugriff"
        text="Die Buchhaltung ist nur für namentlich Berechtigte freigegeben."
      />
    );
  }
  return <Inhalt />;
};
BuchhaltungPage.path = "/buchhaltung";

const BereichLink = ({ name, aktiv, klein, onClick }: { name: string; aktiv: boolean; klein?: boolean; onClick: () => void }) => (
  <button
    type="button"
    aria-current={aktiv ? "page" : undefined}
    onClick={onClick}
    className={cn("cursor-pointer whitespace-nowrap", aktiv ? "font-semibold" : klein ? "text-muted-foreground hover:text-foreground" : "hover:underline")}
  >
    {name}
  </button>
);

const Inhalt = () => {
  const [params, setParams] = useSearchParams();
  const [jahr, setJahr] = useState(
    Number(params.get("jahr")) || new Date().getFullYear(),
  );
  const bereich = params.get("bereich") ?? "start";
  const notify = useNotify();
  const { data: daten, rechnung, neuLaden, error } = useBuchhaltung(jahr);

  const wechsel = (name: string, wert: string) =>
    setParams((p) => {
      p.set(name, wert);
      return p;
    });

  const anlegen = async () => {
    try {
      const vorjahr = daten?.jahre.includes(jahr - 1)
        ? (
            await sb()
              .from("bh_steuerjahr")
              .select("*")
              .eq("jahr", jahr - 1)
              .single()
          ).data
        : null;
      const { updated_at: _, ...alt } = vorjahr ?? { updated_at: null };
      const zeile = {
        ...JAHR_VORGABE(jahr),
        ...alt,
        jahr,
        pendel_satz: JAHR_VORGABE(jahr).pendel_satz,
      };
      pruefe(await sb().from("bh_steuerjahr").insert(zeile));
      await neuLaden();
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
    }
  };

  const excel = async () => {
    if (!rechnung || !daten?.steuerjahr) return;
    const { excelHerunterladen } = await import("./kern/excel");
    const takt = TAKT_NAME[taktAm(daten.takte, `${jahr}-12-31`)];
    await excelHerunterladen(
      rechnung.posten,
      rechnung.punkte,
      daten.steuerjahr,
      takt,
    );
  };

  const jahre = [
    ...new Set([...(daten?.jahre ?? []), jahr, new Date().getFullYear()]),
  ].sort();
  const props: TabProps | null =
    daten && rechnung ? { jahr, daten, ...rechnung, neuLaden } : null;
  const offen =
    rechnung?.posten.filter(
      (p) => !p.kategorie || p.unsicher.length || p.duplikat,
    ).length ?? 0;

  return (
    <div className="mx-auto max-w-7xl space-y-4 px-4 py-4 md:px-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-display flex items-center gap-2 text-2xl font-semibold">
          <Landmark className="h-6 w-6" /> {bereich === "start" ? "Einnahmenüberschuss" : "Buchhaltung"}
        </h1>
        <div className="flex items-center gap-2">
          <Auswahl
            aria-label="Steuerjahr"
            className="w-24"
            value={jahr}
            onChange={(e) => setJahr(Number(e.target.value))}
          >
            {jahre.map((j) => (
              <option key={j} value={j}>
                {j}
              </option>
            ))}
            <option value={jahre[0] - 1}>{jahre[0] - 1}</option>
          </Auswahl>
          {props && (
            <Button variant="outline" onClick={excel}>
              <Download className="h-4 w-4" /> Excel
            </Button>
          )}
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive">
          Daten konnten nicht geladen werden: {error.message}
        </p>
      )}

      {daten && !daten.steuerjahr && (
        <EmptyState
          Icon={Landmark}
          title={`Steuerjahr ${jahr} ist noch nicht angelegt`}
          text="Die Vorgaben lassen sich danach im Setup anpassen."
        >
          <Button className="mt-2" onClick={anlegen}>
            Steuerjahr {jahr} anlegen
          </Button>
        </EmptyState>
      )}

      {props && (
        <>
          <nav aria-label="Bereiche" className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <BereichLink name="Einnahmenüberschuss" aktiv={bereich === "start"} onClick={() => wechsel("bereich", "start")} />
            <span className="text-muted-foreground">·</span>
            {BEREICHE.map(([id, name]) => (
              <BereichLink
                key={id}
                name={id === "rueckfragen" && offen > 0 ? `${name} (${offen})` : name}
                aktiv={bereich === id}
                klein
                onClick={() => wechsel("bereich", id)}
              />
            ))}
          </nav>
          {bereich === "start" && <EinnahmenUeberschuss {...props} wechsel={(b) => wechsel("bereich", b)} />}
          {bereich === "setup" && <SetupTab {...props} />}
          {bereich === "import" && <ImportTab {...props} />}
          {bereich === "erfassung" && <ErfassungTab {...props} />}
          {bereich === "rueckfragen" && <RueckfragenTab {...props} />}
          {bereich === "fahrten" && <FahrtenTab {...props} />}
          {bereich === "auswertung" && <AuswertungTab {...props} />}
        </>
      )}
      <p className="text-xs text-muted-foreground">
        Hilfsmittel, keine Steuerberatung. Stand{" "}
        {heute().split("-").reverse().join(".")}. Werte bitte vor dem Absenden
        in ELSTER prüfen.
      </p>
    </div>
  );
};
